import { useEffect, useState, type RefObject } from "react";

/* Word-local pointer position, written by the shell on every enter/move so
   an overlay never races the pointer events it missed before mounting. */
export interface PointerPos {
  x: number;
  y: number;
  seeded: boolean;
}

export interface OverlayProps {
  hostRef: RefObject<HTMLSpanElement | null>;
  posRef: RefObject<PointerPos>;
  hot: boolean;
  text: string;
  /* tap = coarse pointer: no live pointer to follow, one-shot play */
  tap: boolean;
}

/* The exit budget every overlay's bow-out choreography fits inside — the
   shell unmounts after it, and the scss reads it back as --hw-exit so the
   staggered reverse animations and the final container fade share one
   clock. AboutName's live selection uses it too. */
export const EXIT_MS = 280;

/* Line-box height at which a word gets the full chrome (Figma label +
   badge, the editor gutter + IntelliSense, the labelled n8n fork). The home display
   headline is ≥40px on every phone (13vw floor at 320px ≈ 42px); the
   statement-size words are ~30px. One threshold, no device-width flips. */
export const BIG_H = 40;

/* Frame-rate independent lerp factor: `k` is tuned per 60Hz frame, so a
   120Hz display follows at the same speed in wall-clock time. */
export const lerpK = (k: number, dt: number) =>
  1 - (1 - k) ** ((dt * 60) / 1000);

let axisCtx: CanvasRenderingContext2D | null = null;

/* The line's optical axis: the middle of the capitals, measured from the
   word's own font, so an overlay centres on the letters rather than on the
   line box (whose top carries the ascender air). Falls back to the box
   middle where canvas metrics are unavailable. */
function capAxis(el: HTMLElement, h: number) {
  axisCtx ??= document.createElement("canvas").getContext("2d");
  if (!axisCtx) return h / 2;
  const cs = getComputedStyle(el);
  axisCtx.font = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
  const m = axisCtx.measureText("H");
  const asc = m.fontBoundingBoxAscent;
  const desc = m.fontBoundingBoxDescent;
  if (!asc) return h / 2;
  const baseline = (h - (asc + desc)) / 2 + asc;
  return baseline - m.actualBoundingBoxAscent / 2;
}

/* Word-box size in CSS px plus its optical axis, measured once on mount —
   overlays mount on hover, so fonts are loaded and layout is settled. */
export function useWordBox(hostRef: RefObject<HTMLSpanElement | null>) {
  const [box, setBox] = useState<{
    w: number;
    h: number;
    axis: number;
  } | null>(null);
  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setBox({ w: r.width, h: r.height, axis: capAxis(el, r.height) });
  }, [hostRef]);
  return box;
}
